import React, { useContext, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { 
  Box, Drawer, AppBar, Toolbar, List, CssBaseline, 
  Typography, Divider, IconButton, Avatar, Menu, MenuItem, useMediaQuery 
} from '@mui/material';

import MenuIcon from '@mui/icons-material/Menu';
import ListItemIcon from '@mui/material/ListItemIcon';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';

// Imágenes
import logoQUD from '../../assets/images/logos/logoQUD.png'; 
import { imagesURL } from '../../apiRoutes/apiBase';
import AuthContext from '../../navigation/context/authContext';
import UserContext from '../../navigation/context/userContext';
import { useHistory, useLocation } from 'react-router-dom';
import Copyright from '../../components/copyRight';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStore, faUser, faShieldAlt } from '@fortawesome/free-solid-svg-icons';

import MainListItems from './mainList';
import FinanceListItems from './financesList';
import SecurityListItems from './securityList';
import ManagementListItems from './management';

import en_language_img from '../../assets/images/countries/usa.png';
import es_language_img from '../../assets/images/countries/spain.png';

const DRAWER_WIDTH = 280; 

export default function CompanyLayout(props) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { userData } = useContext(UserContext);
  const { signOut } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();

  const isMobile = useMediaQuery(theme.breakpoints.down('md')); 
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const redirect = (_route) => history.push(_route);
  const handleLanguage = (lang) => i18n.changeLanguage(lang);
  const handleMenu = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSignOut = () => {
    history.replace('/');
    signOut();
  };

  // Título dinámico simple según la ruta (puedes expandirlo)
  const getPageTitle = () => {
      const path = location.pathname;
      if (path === '/') return 'Dashboard';
      if (path.includes('/profile')) return 'Business Profile';
      if (path.includes('/products')) return 'Productos';
      if (path.includes('/reservations')) return 'Reservaciones';
      return 'Dashboard';
  };

  const userInitial = userData.user.name ? userData.user.name.charAt(0).toUpperCase() : 'U';

  // Agrega esta pequeña función antes de tu drawerContent
  const handleMenuItemClick = () => {
      if (isMobile) {
          setMobileOpen(false); // Cierra el menú en móviles/tablets
      }
  };

  // --- CONTENIDO DEL SIDEBAR (Estilo 100% GetBites) ---
  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      
      {/* Logo Area */}
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <img src={logoQUD} style={{ height: 28 }} alt="QUDLogo" /> 
          <Typography variant="caption" sx={{ color: '#888', mt: 0.5, fontWeight: 'bold', letterSpacing: 0.5 }}>
              BUSINESS
          </Typography>
      </Box>

      <Divider sx={{ mb: 1, mx: 3, opacity: 0.5 }} />
      
      {/* Listas de Navegación (Con CSS para ocultar el scrollbar feo) */}
      <Box sx={{ 
          flexGrow: 1, 
          overflowY: 'auto',
          px: 2, // Padding horizontal para que las opciones no toquen los bordes
          '&::-webkit-scrollbar': { display: 'none' }, // Oculta scrollbar en Chrome/Safari
          scrollbarWidth: 'none' // Oculta scrollbar en Firefox
      }}>
        <List disablePadding>
            <MainListItems onItemClick={handleMenuItemClick} />
            
            <Box sx={{ mt: 3, mb: 1, ml: 1 }}>
                <Typography variant='caption' sx={{ color: '#aaa', fontWeight: 700, letterSpacing: 1, fontSize: '0.7rem' }}>
                    GESTIÓN
                </Typography>
            </Box>
            <ManagementListItems onItemClick={handleMenuItemClick} />

            {(userData.user.type === 'company' || userData.user.idTipoUsuario === 2) && (
                <>
                    <Box sx={{ mt: 3, mb: 1, ml: 1 }}>
                        <Typography variant='caption' sx={{ color: '#aaa', fontWeight: 700, letterSpacing: 1, fontSize: '0.7rem' }}>
                            FINANZAS
                        </Typography>
                    </Box>
                    <FinanceListItems />
                </>
            )}

            <Box sx={{ mt: 3, mb: 1, ml: 1 }}>
                <Typography variant='caption' sx={{ color: '#aaa', fontWeight: 700, letterSpacing: 1, fontSize: '0.7rem' }}>
                    SISTEMA
                </Typography>
            </Box>
            <SecurityListItems />
        </List>
      </Box>
      
      {/* Bottom User Profile in Sidebar (Píldora flotante estilo GetBites) */}
      <Box sx={{ p: 2 }}>
          <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              p: 1.5, 
              borderRadius: 3, // Borde redondeado
              bgcolor: '#f8f9fa' // Fondo gris clarito
          }}>
              <Avatar 
                sx={{ width: 40, height: 40, bgcolor: theme.palette.primary.main, fontSize: 16 }} 
                src={imagesURL + 'empresas/' + userData.entity.profile_image}
              >
                {userData.entity.last_name ? userData.entity.last_name.charAt(0).toUpperCase() : 'B'}
              </Avatar>
              <Box sx={{ ml: 2, overflow: 'hidden' }}>
                  <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, color: '#333' }}>
                      {userData.entity.last_name || userData.entity.name}
                  </Typography>
                  <Typography variant="caption" noWrap sx={{ color: '#888', display: 'block' }}>
                      {userData.user.type === 'company' ? 'Business Access' : 'Staff'}
                  </Typography>
              </Box>
          </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#fefefe' }}>
        <CssBaseline />

        {/* --- APP BAR --- */}
        <AppBar 
            position="fixed" 
            elevation={0}
            sx={{ 
                width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
                ml: { md: `${DRAWER_WIDTH}px` },
                backgroundColor: '#ffffff',
                color: '#333',
                borderBottom: '1px solid #e0e0e0',
                boxShadow: 'none',
				borderRadius:0
            }}
        >
            <Toolbar sx={{ minHeight: '64px !important', px: { xs: 2, sm: 4 } }}>
                <IconButton
                    color="inherit"
                    edge="start"
                    onClick={handleDrawerToggle}
                    sx={{ mr: 2, display: { md: 'none' }, color: '#333' }}
                >
                    <MenuIcon />
                </IconButton>
                
                {/* Título de la página (A la izquierda, como en GetBites) */}
                <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 700, fontSize: '1.1rem' }}>
                    {getPageTitle()}
                </Typography>

                {/* MENÚ DE USUARIO TOP RIGHT */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    
                    {/* Botón Circular del Usuario (Color azul vibrante) */}
                    <IconButton onClick={handleMenu} sx={{ p: 0 }}>
                        <Avatar 
                            sx={{ 
                                bgcolor: theme.palette.primary.main, // Color azul
                                color: '#FFF',
                                width: 36, 
                                height: 36, 
                                fontSize: '1rem', 
                                fontWeight: 'bold' 
                            }}
                            src={userData.user.profile_image ? `${imagesURL}users/${userData.user.profile_image}` : null}
                        >
                            {userInitial}
                        </Avatar>
                    </IconButton>

                    {/* El Menú Desplegable */}
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                        onClick={handleClose}
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                overflow: 'visible',
                                filter: 'drop-shadow(0px 4px 20px rgba(0,0,0,0.08))',
                                mt: 1.5,
                                borderRadius: 3,
                                minWidth: 220,
                                '&:before': {
                                    content: '""', display: 'block', position: 'absolute', top: 0, right: 14,
                                    width: 10, height: 10, bgcolor: 'background.paper',
                                    transform: 'translateY(-50%) rotate(45deg)', zIndex: 0,
                                },
                            },
                        }}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    >
                        <Box sx={{ px: 2, py: 1.5 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                                {userData.user.name} {userData.user.last_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {userData.user.email}
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: 1 }} />

                        <MenuItem onClick={() => { handleClose(); redirect('/profile'); }}>
                            <ListItemIcon><FontAwesomeIcon icon={faUser} color="#757575" /></ListItemIcon>
                            <Typography variant="body2" fontWeight="500">Mi Perfil</Typography>
                        </MenuItem>
                        
                        <MenuItem onClick={() => { handleClose(); redirect('/security'); }}>
                            <ListItemIcon><FontAwesomeIcon icon={faShieldAlt} color="#757575" /></ListItemIcon>
                            <Typography variant="body2" fontWeight="500">Seguridad</Typography>
                        </MenuItem>

                        <MenuItem onClick={() => { handleClose(); handleLanguage(i18n.language === 'es' ? 'en' : 'es'); }}>
                            <img src={i18n.language === 'es' ? en_language_img : es_language_img} alt='language' style={{ width: 20, marginRight: 15 }} />
                            <Typography variant="body2" fontWeight="500">{i18n.language === 'es' ? 'English' : 'Español'}</Typography>
                        </MenuItem>

                        <Divider sx={{ my: 1 }} />
                        
                        <MenuItem onClick={handleSignOut} sx={{ color: 'error.main' }}>
                            <ListItemIcon><ExitToAppIcon fontSize="small" color="error" /></ListItemIcon>
                            <Typography variant="body2" fontWeight="bold">Cerrar Sesión</Typography>
                        </MenuItem>
                    </Menu>
                </Box>
            </Toolbar>
        </AppBar>

        {/* --- SIDEBAR NAV --- */}
        <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{ keepMounted: true }} 
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH, bgcolor: '#FFF', borderRight: 'none' },
                }}
            >
                {drawerContent}
            </Drawer>
            <Drawer
                variant="permanent"
                sx={{
                    display: { xs: 'none', md: 'block' },
                    '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH, bgcolor: '#FFF', borderRight: '1px solid #e0e0e0' },
                }}
                open
            >
                {drawerContent}
            </Drawer>
        </Box>

        {/* --- MAIN CONTENT AREA --- */}
        <Box
			component="main"
			sx={{
				flexGrow: 1,
				p: {xs: 2, sm: 4},
				width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
				mt: '74px'
			}}
		>
            {props.children}
			<br/>
            <Copyright />
        </Box>
    </Box>
  );
}